import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CssBaseline from "@material-ui/core/CssBaseline";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Container from "@material-ui/core/Container";
import { useHistory, Redirect } from "react-router-dom";
// import CircularProgress from "@material-ui/core/CircularProgress";
import ProgressBar from "sushiweb/ProgressBar";
import { matchPath } from "react-router";
import styled from "styled-components";
import { FlexContainer } from "../../../constants/assets/containers";

import * as UserActions from "../../../store/actions/User";
import Toast from "../../Common/Toast";
import DoubleArrowRight from "sushiweb/Icons/all/DoubleArrowRight";
import DoubleArrowLeft from "sushiweb/Icons/all/DoubleArrowLeft";
import * as MenuActionsActions from "../../../store/actions/MenuActions";
import FullPageLoader from "../../Common/FullPageLoader";
import NavHeader from "./NavHeader";
import routes from "../../../constants/RoutesData";
import { getPath, isMobile } from "../../../utils";
import ErrorMsgs from "../../../constants/Errors.json";
import variables from "../../../constants/assets/variables";
import NavSidePanel from "./NavSidePanel";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  toolbarHeight: {
    minHeight: "67px",
  },
  boldText: {
    fontWeight: "bolder",
  },
  loaderMask: {
    position: "fixed",
    top: 0,
    left: 0,
    backgroundColor: "rgba(0,0,0,0.8)",
    zIndex: 2000,
    width: "100%",
    height: "100%",
  },
  grow: {
    flexGrow: 1,
  },
  appHeader: {
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
  activeNavItem: {
    borderBottom: "3px solid white",
    borderRadius: 0,
    paddingBottom: "18px",
    paddingTop: "21px",
  },
  navItem: {
    paddingTop: "21px",
    paddingBottom: "21px",
  },
}));
function MainNavBar(props) {
  const classes = useStyles();
  const history = useHistory();
  const [authorized, setAuthorized] = useState(true);
  const { setSideBarCollapsed, isSidebarCollapsed } = props;
  // const [isCollapsed, setIsCollapsed] = useState(false);

  // it returns the current path, in most cases it will be history.location.pathname,
  // if the route has params like `/courses/28`, then it will return `/courses/:id`.
  // if no route matches, then it returns `undefined`.
  const getCurrentPath = () => {
    const pathname = history.location.pathname;
    if (getPath(routes, `routesData.${pathname}`, undefined)) {
      return pathname;
    }
    let match;
    for (const path of routes.routesWithParams) {
      match = matchPath(pathname, { path: path, exact: true });
      if (getPath(match, "isExact", false) === true) {
        return path;
      }
    }
    return undefined;
  };

  // this indicates that whether the permissible actions for current route has been fetched or not.
  const [fetchedActions, setFetchedActions] = useState(false);
  const currentPath = getCurrentPath();

  // if the route is not permitted, redirect to this path.
  const redirectPath = getPath(
    routes,
    `routesData.${currentPath}.redirectPath`,
    "/"
  );

  const minAllowedAction = getPath(
    routes,
    `routesData.${currentPath}.minAllowedAction`,
    undefined
  );
  const menuName = getPath(
    routes,
    `routesData.${currentPath}.menuName`,
    undefined
  );
  const randomName = getPath(routes, `routesData.${currentPath}`);

  const handleSidePanelCollapse = () => {
    setSideBarCollapsed(!isSidebarCollapsed);
  };

  const CollapseButton = () => {
    return (
      <CollapseIconholder onClick={handleSidePanelCollapse} justify="center">
        {isSidebarCollapsed ? <DoubleArrowRight /> : <DoubleArrowLeft />}
      </CollapseIconholder>
    );
  };

  useEffect(() => {
    if (Object.keys(props.status).length > 0) {
      setTimeout(() => props.setStatus({}), 3000);
    }
  }, [props.status]);

  // fetch permissible actions for each menu (if not already fetched).
  // don't do this for homepage.
  useEffect(() => {
    if (
      Object.keys(getPath(props, `menuActions.${menuName}`, {})).length === 0 &&
      currentPath !== "/" &&
      menuName !== undefined
    ) {
      props
        .checkMenuActionsAllowed(menuName)
        .then((res) => setFetchedActions(true));
    }
  }, [history.location.pathname]);

  // reset state `fetchedActions` to `false` on every route change to allow fetching actions again.
  // if actions are already fetched, change the value of `fetchedActions` to `true`
  useEffect(() => {
    if (
      menuName === undefined ||
      Object.keys(getPath(props, `menuActions.${menuName}`, {})).length > 0
    ) {
      setFetchedActions(true);
    } else {
      setFetchedActions(false);
    }
  }, [history.location.pathname]);

  const Navbar = (
    <NavigationSection>
      <NavHeader menus={props.menus} />
    </NavigationSection>
  );

  // show <FullPageLoader /> when fetching menus or permissible menu actions.
  // don't show any loader on homepage.
  if (
    history.location.pathname !== "/" &&
    (props.menus.size === 0 || fetchedActions === false)
  ) {
    return <FullPageLoader size={100} />;
  }

  // redirect to homepage if the current route is not permitted and show error to the user.
  if (
    history.location.pathname !== "/" &&
    ((props.menus.size > 0 && !props.menus.has(menuName)) ||
      (Object.keys(getPath(props, `menuActions.${menuName}`, {})).length > 0 &&
        getPath(props, `menuActions.${menuName}.${minAllowedAction}`, false) ===
          false))
  ) {
    let errorMsg = ErrorMsgs.INSUFFICIENT_PERMISSIONS;
    if (menuName === undefined) {
      errorMsg = ErrorMsgs.NOT_FOUND;
    }
    setTimeout(
      () =>
        props.setStatus({
          type: "error",
          message: errorMsg,
        }),
      0
    );
    return <Redirect to={redirectPath} />;
  }

  const removePadding = props.removePadding;

  if (authorized) {
    return (
      <React.Fragment>
        <CssBaseline />
        {Navbar}
        <FlexContainerExtended align="flex-start">
          {!isMobile() && (
            <FlexContainer.LeftSection collapse={isSidebarCollapsed}>
              <CollapsibleMenu>
                <NavSidePanel menus={props.menus} />
                <CollapseButton />
              </CollapsibleMenu>
            </FlexContainer.LeftSection>
          )}
          <FlexContainer.RightSection
            removePadding={removePadding}
            collapse={isSidebarCollapsed}
          >
            {props.children}
          </FlexContainer.RightSection>
        </FlexContainerExtended>
        {Object.keys(props.status).length > 0 && <Toast {...props.status} />}
        {props.isLoading && (
          <ProgressBar id="sushi-progressBar" active={true} />
        )}
      </React.Fragment>
    );
  } else if (authorized === false) {
    return "";
  }
}

MainNavBar.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
};

const CollapseIconholder = styled(FlexContainer)`
  position: absolute;
  top: -15px;
  right: -30px;
  width: 30px;
  border-bottom-right-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: 2px 2px rgb(0 0 0 / 10%);
  height: 30px;
  background: ${variables.white};
  cursor: pointer;
`;

const NavigationSection = styled.section`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  background: ${variables.white};
  z-index: 3;
`;

const FlexContainerExtended = styled(FlexContainer)`
  height: calc(100vh - 70px);
  margin-top: 70px;
  overflow: hidden;
`;

const CollapsibleMenu = styled.div`
  position: relative;
`;

FlexContainer.LeftSection = styled.div`
  transition: 0.5s all ease-in-out;
  flex: ${({ collapse }) => (collapse ? "0" : "0 1 20%")};
  width: ${({ collapse }) => (collapse ? "0" : "100%")};
  height: calc(100vh - 70px);
  box-shadow: 0px 8px 12px rgba(28, 28, 28, 0.08);
  padding: 15px 0 24px;
  background: #fff;
`;

FlexContainer.RightSection = styled.div`
  padding: ${({ removePadding }) =>
    removePadding ? "0px" : isMobile() ? "10px" : "24px"};
  flex: ${({ collapse }) => (isMobile() || collapse ? "0 1 100%" : "0 1 80%")};
  height: 100%;
  overflow: scroll;
`;

function mapStateToProps(state) {
  const { User, MenuActions } = state;
  return {
    status: User.status,
    isLoading: User.isLoading,
    isSidebarCollapsed: User.isSidebarCollapsed,
    menuActions: MenuActions.menuActions,
    menus: MenuActions.menus,
    allMenuActions: MenuActions.allMenuActions,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    isUserValid: bindActionCreators(UserActions.isUserValid, dispatch),
    setStatus: bindActionCreators(UserActions.setStatus, dispatch),
    checkMenuActionsAllowed: bindActionCreators(
      MenuActionsActions.checkMenuActionsAllowed,
      dispatch
    ),
    setSideBarCollapsed: bindActionCreators(
      UserActions.setSideBarCollapsed,
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MainNavBar);
